import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { AppProvider, useApp } from "@/lib/store";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShoppingBag, User, Minus, Plus, Trash2, X, MapPin, Menu, Globe } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Navigate } from "react-router-dom";
import { RESTAURANT_LOCATIONS } from "@/lib/locations";
import HomePage from "@/pages/HomePage";
import MenuPage from "@/pages/MenuPage";
import ProductDetail from "@/pages/ProductDetail";
import AuthPage from "@/pages/AuthPage";
import RegisterPage from "@/pages/RegisterPage";
import CheckoutPage from "@/pages/CheckoutPage";
import OrderTrackingPage from "@/pages/OrderTrackingPage";
import OrderHistoryPage from "@/pages/OrderHistoryPage";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AboutUsPage from "@/pages/AboutUsPage";
import ProfilePage from "@/pages/ProfilePage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import CookieBanner from "@/components/CookieBanner";
import ThemeToggle from "@/components/ThemeToggle";
import { useTheme } from "@/lib/theme";

function CartSheet() {
  const { cart, cartOpen, setCartOpen, removeFromCart, updateCartItem, estimateCartTotal, cartItemCount, t } = useApp();
  const navigate = useNavigate();
  const [unavailableProductIds, setUnavailableProductIds] = useState(new Set());

  useEffect(() => {
    if (!cartOpen || cart.length === 0) {
      setUnavailableProductIds(new Set());
      return;
    }

    let mounted = true;
    const loadAvailability = async () => {
      try {
        const res = await api.get("/menu/products");
        const outIds = new Set(
          (res.data || [])
            .filter((p) => p.out_of_stock)
            .map((p) => p.id)
        );
        if (mounted) setUnavailableProductIds(outIds);
      } catch {
        if (mounted) setUnavailableProductIds(new Set());
      }
    };

    loadAvailability();
    return () => {
      mounted = false;
    };
  }, [cartOpen, cart]);

  const hasUnavailableItems = cart.some((item) => unavailableProductIds.has(item.product?.id));

  const handleCheckout = () => {
    if (hasUnavailableItems) {
      toast.error(t("One or more items are out of stock. Remove them before checkout."));
      return;
    }
    setCartOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent className="w-full sm:max-w-md bg-[#0A0A0A] border-l border-[#2A2A2E] flex flex-col p-0" data-testid="cart-sheet">
        <div className="px-6 pt-6 pb-4 border-b border-[#27272A]">
          <SheetTitle className="text-[#EDEDED] font-playfair text-xl">{t("Your Cart")}</SheetTitle>
        </div>
        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-[#A8ABB2] gap-3 px-6">
            <ShoppingBag className="w-12 h-12 opacity-30" />
            <p className="text-sm">{t("Your cart is empty")}</p>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 px-4">
              <div className="space-y-3 py-4">
                {cart.map((item, idx) => {
                  let itemPrice = item.product?.base_price || 0;
                  (item.modifiers || []).forEach(m => { itemPrice += (m.price || 0) * (m.quantity || 1); });
                  (item.selectedOptions || []).forEach(o => { itemPrice += o.price || 0; });
                  return (
                    <motion.div
                      key={item.cartId}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="bg-[#121212] rounded-xl p-4 border border-[#27272A] group"
                      data-testid={`cart-item-${item.cartId}`}
                    >
                      <div className="flex gap-3">
                        {item.product?.image_url && (
                          <img src={item.product.image_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start">
                            <p className="font-semibold text-[#EDEDED] text-sm">{item.product?.name}</p>
                            <button onClick={() => removeFromCart(item.cartId)} className="text-[#3F3F46] hover:text-[#EF4444] transition-colors ml-2" data-testid={`remove-cart-item-${item.cartId}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          {unavailableProductIds.has(item.product?.id) && (
                            <div className="inline-flex items-center mt-1 bg-[#DC2626]/15 border border-[#DC2626]/40 text-[#FCA5A5] text-[10px] font-semibold px-2 py-0.5 rounded-md">
                              {t("Out of stock")}
                            </div>
                          )}
                          {item.removedIngredients?.length > 0 && (
                            <p className="text-[10px] text-[#A8ABB2] mt-0.5">
                              Remove: {item.removedIngredients.map(rid => {
                                const ing = item.product?.ingredients?.find(i => i.id === rid);
                                return ing?.name || rid;
                              }).join(", ")}
                            </p>
                          )}
                          {item.selectedOptions?.length > 0 && (
                            <p className="text-[10px] text-[#D4D4D8] mt-0.5">
                              {item.selectedOptions.map(o => o.name).join(", ")}
                            </p>
                          )}
                          {item.modifiers?.length > 0 && (
                            <p className="text-[10px] text-[#D4B483] mt-0.5">
                              {item.modifiers.map(m => `+${m.name}${m.quantity > 1 ? ` x${m.quantity}` : ""}`).join(", ")}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between items-center mt-3 pt-3 border-t border-[#1A1A1A]">
                        <div className="flex items-center gap-2">
                          <button className="w-7 h-7 rounded-full bg-[#1A1A1A] border border-[#27272A] flex items-center justify-center text-[#EDEDED] hover:border-[#3F3F46] transition-all active:scale-90"
                            onClick={() => item.quantity > 1 && updateCartItem(item.cartId, { ...item, quantity: item.quantity - 1 })} data-testid={`cart-qty-minus-${item.cartId}`}>
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="text-sm font-bold text-[#EDEDED] w-6 text-center font-mono-price">{item.quantity}</span>
                          <button className="w-7 h-7 rounded-full bg-[#D4B483] flex items-center justify-center text-[#0A0A0A] hover:bg-[#E5CFA5] transition-all active:scale-90"
                            onClick={() => updateCartItem(item.cartId, { ...item, quantity: item.quantity + 1 })} data-testid={`cart-qty-plus-${item.cartId}`}>
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <span className="font-mono-price text-sm text-[#D4B483] font-bold price-glow">{(itemPrice * item.quantity).toFixed(0)} kr</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </ScrollArea>
            <div className="border-t border-[#27272A] p-6 space-y-4 bg-[#121212]">
              {hasUnavailableItems && (
                <div className="bg-[#DC2626]/10 border border-[#DC2626]/40 text-[#FCA5A5] text-xs font-semibold rounded-lg px-3 py-2">
                  {t("Some items in your cart are out of stock. Remove them to continue.")}
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-[#D4D4D8] text-sm">{t("Estimated Total")}</span>
                <span className="font-mono-price text-xl font-bold text-[#EDEDED] price-glow">{estimateCartTotal().toFixed(0)} kr</span>
              </div>
              <Button onClick={handleCheckout} disabled={hasUnavailableItems} className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-bold h-12 text-base btn-glow rounded-xl disabled:opacity-50" data-testid="cart-checkout-btn">
                {hasUnavailableItems ? t("Remove sold out items") : `${t("Checkout")} (${cartItemCount} ${cartItemCount === 1 ? t("item") : t("items")})`}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function useNavLinks() {
  const { t } = useApp();
  const location = useLocation();

  const links = [
    { id: "home", to: "/", label: t("Hem") },
    { id: "menu", to: "/menu", label: t("Meny") },
    { id: "orders", to: "/orders", label: t("Orders") },
    { id: "about", to: "/about-us", label: t("Om oss") },
  ];

  const isActive = (id) => {
    const p = location.pathname;
    if (id === "home") return p === "/";
    if (id === "menu") return p === "/menu" || p.startsWith("/product/") || p === "/checkout";
    if (id === "orders") return p === "/orders" || p.startsWith("/order/");
    if (id === "about") return p === "/about-us";
    return false;
  };

  return { links, isActive };
}

function MobileMenu({ open, onClose }) {
  const { user, t, language, setLanguage } = useApp();
  const { links, isActive } = useNavLinks();

  const canSwitchToAdmin = typeof window !== "undefined"
    && ["localhost", "127.0.0.1"].includes(window.location.hostname)
    && Boolean(localStorage.getItem("biten_admin"));

  const currentRestId = localStorage.getItem("biten_restaurant_id");
  const restName = RESTAURANT_LOCATIONS.find(loc => loc.id === currentRestId)?.name || "";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[70] lg:hidden"
          data-testid="mobile-menu"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "tween", duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-y-0 left-0 w-full max-w-[340px] bg-[#0C0C0D] border-r border-[#D4B483]/15 flex flex-col shadow-2xl"
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-5 border-b border-[#1A1A1A]">
              <div className="flex items-center gap-3">
                <img src="/assets/logo/logo.jpg" alt="Bästa Biten" className="h-10 w-10 object-cover rounded-full border border-[#D4B483]/30" />
                <div className="leading-none">
                  <p className="font-playfair text-base font-bold tracking-[0.14em] text-[#EDEDED]">BÄSTA BITEN</p>
                  {restName && <p className="text-[9px] uppercase tracking-[0.34em] text-[#D4B483] mt-1.5">{restName}</p>}
                </div>
              </div>
              <button onClick={onClose} aria-label={t("Stäng")} className="p-2 rounded-full text-[#D4D4D8] hover:text-[#EDEDED] hover:bg-[#1A1A1A] transition-colors" data-testid="mobile-menu-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Nav links */}
            <nav className="flex-1 overflow-y-auto px-2 py-6">
              {links.map(({ id, to, label }, i) => (
                <motion.div
                  key={id}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.35, ease: "easeOut" }}
                >
                  <Link
                    to={to}
                    onClick={onClose}
                    className={`group flex items-baseline gap-4 px-5 py-3.5 rounded-xl transition-colors ${isActive(id) ? "bg-[#D4B483]/[0.07]" : "hover:bg-[#141415]"}`}
                    data-testid={`mobile-nav-${id}`}
                  >
                    <span className={`font-mono-price text-[10px] tracking-widest ${isActive(id) ? "text-[#D4B483]" : "text-[#A8ABB2] group-hover:text-[#D4B483]/70"}`}>
                      0{i + 1}
                    </span>
                    <span className={`font-playfair text-[1.65rem] leading-none transition-colors ${isActive(id) ? "text-[#D4B483]" : "text-[#EDEDED] group-hover:text-[#E5CFA5]"}`}>
                      {label}
                    </span>
                    {isActive(id) && <span className="ml-auto self-center w-1.5 h-1.5 rounded-full bg-[#D4B483]" />}
                  </Link>
                </motion.div>
              ))}
            </nav>

            {/* Drawer footer */}
            <div className="px-6 pb-8 pt-5 border-t border-[#1A1A1A] space-y-4">
              <Link
                to={user ? "/profile" : "/auth"}
                onClick={onClose}
                className="flex items-center justify-center gap-2 w-full h-12 rounded-xl bg-[#D4B483] text-[#0A0A0A] font-bold text-sm btn-glow transition-colors hover:bg-[#E5CFA5]"
                data-testid="mobile-nav-profile"
              >
                <User className="w-4 h-4" />
                {user ? user.full_name : t("Logga in")}
              </Link>
              <div className="flex items-center justify-between gap-3">
                {restName ? (
                  <button
                    onClick={() => { localStorage.removeItem("biten_restaurant_id"); window.location.href = "/"; }}
                    className="flex items-center gap-1.5 text-[11px] text-[#D4D4D8] hover:text-[#D4B483] transition-colors"
                  >
                    <MapPin className="w-3.5 h-3.5 text-[#D4B483]" />
                    {restName}
                    <span className="text-[#A8ABB2] underline underline-offset-2 ml-1">{t("Byt")}</span>
                  </button>
                ) : <span />}
                <div className="flex items-center gap-1 text-[11px]">
                  <Globe className="w-3.5 h-3.5 text-[#A8ABB2] mr-0.5" />
                  <button onClick={() => setLanguage("sv")} className={`px-1.5 py-0.5 rounded transition-colors ${language === "sv" ? "text-[#D4B483] font-bold" : "text-[#A8ABB2] hover:text-[#D4D4D8]"}`}>SV</button>
                  <span className="text-[#27272A]">/</span>
                  <button onClick={() => setLanguage("en")} className={`px-1.5 py-0.5 rounded transition-colors ${language === "en" ? "text-[#D4B483] font-bold" : "text-[#A8ABB2] hover:text-[#D4D4D8]"}`}>EN</button>
                </div>
              </div>
              {canSwitchToAdmin && (
                <Link to="/manage" onClick={onClose} className="block text-center text-[11px] text-[#A8ABB2] hover:text-[#D4D4D8] transition-colors">
                  {t("Admin")}
                </Link>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Header() {
  const { cartItemCount, setCartOpen, user, t } = useApp();
  const { links, isActive } = useNavLinks();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const canSwitchToAdmin = typeof window !== "undefined"
    && ["localhost", "127.0.0.1"].includes(window.location.hostname)
    && Boolean(localStorage.getItem("biten_admin"));

  const currentRestId = localStorage.getItem("biten_restaurant_id");
  const restName = RESTAURANT_LOCATIONS.find(loc => loc.id === currentRestId)?.name || "";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "lux-header-scrolled" : "lux-header"}`} data-testid="main-header">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-8 h-16 md:h-20 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          {/* Left: burger (mobile) / brand (desktop) */}
          <div className="flex items-center justify-start">
            <button
              onClick={() => setMenuOpen(true)}
              aria-label={t("Öppna meny")}
              className="lg:hidden p-2.5 -ml-1 rounded-full text-[#EDEDED] hover:bg-[#1A1A1A] hover:text-[#D4B483] transition-colors"
              data-testid="burger-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/" className="hidden lg:flex items-center gap-3 group" data-testid="logo-link">
              <img src="/assets/logo/logo.jpg" alt="Bästa Biten" className="h-11 w-11 object-cover rounded-full border border-[#D4B483]/30 shadow-sm transition-transform duration-500 group-hover:scale-105" />
              <div className="leading-none">
                <p className="font-playfair text-lg font-bold tracking-[0.16em] text-[#EDEDED]">BÄSTA BITEN</p>
                <p className="text-[8.5px] uppercase tracking-[0.4em] text-[#D4B483] mt-1.5">{restName || "Restaurang"}</p>
              </div>
            </Link>
          </div>

          {/* Center: logo (mobile) / nav (desktop) */}
          <div className="flex items-center justify-center">
            <Link to="/" className="lg:hidden flex items-center gap-2.5" data-testid="logo-link-mobile">
              <img src="/assets/logo/logo.jpg" alt="Bästa Biten" className="h-10 w-10 object-cover rounded-full border border-[#D4B483]/30 shadow-sm" />
              <span className="font-playfair text-base font-bold tracking-[0.12em] text-[#EDEDED] hidden sm:block">BÄSTA BITEN</span>
            </Link>
            <nav className="hidden lg:flex items-center gap-9" data-testid="desktop-nav">
              {links.map(({ id, to, label }) => (
                <Link
                  key={id}
                  to={to}
                  className={`lux-nav-link text-[11px] font-bold uppercase tracking-[0.24em] transition-colors duration-300 ${isActive(id) ? "text-[#D4B483] lux-nav-link-active" : "text-[#D4D4D8] hover:text-[#EDEDED]"}`}
                  data-testid={`nav-${id}`}
                >
                  {label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Right: actions */}
          <div className="flex items-center justify-end gap-1 md:gap-2.5">
            {restName && (
              <button
                onClick={() => { localStorage.removeItem("biten_restaurant_id"); window.location.href = "/"; }}
                className="hidden xl:flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-3 py-2 rounded-full border border-[#27272A] text-[#D4D4D8] hover:text-[#D4B483] hover:border-[#D4B483]/40 transition-colors"
                title={t("Byt restaurang")}
              >
                <MapPin className="w-3 h-3 text-[#D4B483]" />
                {restName}
              </button>
            )}
            {canSwitchToAdmin && (
              <Link
                to="/manage"
                className="hidden lg:block text-[10px] uppercase tracking-[0.18em] px-3 py-2 rounded-full border border-[#27272A] text-[#D4D4D8] hover:text-[#EDEDED] hover:bg-[#1A1A1A] transition-colors"
              >
                {t("Admin")}
              </Link>
            )}
            <ThemeToggle />
            <Link
              to={user ? "/profile" : "/auth"}
              className="hidden lg:flex items-center gap-2 p-2.5 rounded-full hover:bg-[#1A1A1A] border border-transparent hover:border-[#D4B483]/25 transition-colors group"
              data-testid="nav-profile"
              title={user ? t("Profil") : t("Logga in")}
            >
              <User className="w-[18px] h-[18px] text-[#EDEDED] group-hover:text-[#D4B483] transition-colors" />
            </Link>
            <button onClick={() => setCartOpen(true)} className="relative p-2.5 rounded-full hover:bg-[#1A1A1A] transition-colors border border-transparent hover:border-[#D4B483]/25 group" data-testid="cart-icon-btn">
              <ShoppingBag className="w-5 h-5 text-[#EDEDED] group-hover:text-[#D4B483] transition-colors" />
              {cartItemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#D4B483] text-[#0A0A0A] text-[10px] rounded-full flex items-center justify-center font-bold shadow-[0_0_10px_rgba(212,180,131,0.5)]">
                  {cartItemCount}
                </span>
              )}
            </button>
          </div>
        </div>
        {/* Gold hairline */}
        <div className={`h-px bg-gradient-to-r from-transparent via-[#D4B483]/40 to-transparent transition-opacity duration-500 ${scrolled ? "opacity-100" : "opacity-0"}`} />
      </header>
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}

function Footer() {
  const { user, t } = useApp();
  const { links } = useNavLinks();

  return (
    <footer className="relative z-[1] mt-20 border-t border-[#D4B483]/10 bg-[#0C0C0D]">
      <div className="max-w-7xl mx-auto px-6 md:px-8 pt-14 pb-10">
        <div className="grid gap-10 md:grid-cols-[1.4fr_1fr_1fr]">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src="/assets/logo/logo.jpg" alt="Bästa Biten" className="h-11 w-11 object-cover rounded-full border border-[#D4B483]/30" />
              <div className="leading-none">
                <p className="font-playfair text-lg font-bold tracking-[0.16em] text-[#EDEDED]">BÄSTA BITEN</p>
                <p className="text-[8.5px] uppercase tracking-[0.4em] text-[#D4B483] mt-1.5">{t("Restaurang & Catering")}</p>
              </div>
            </div>
            <p className="text-[#A8ABB2] text-sm leading-relaxed max-w-xs">
              {t("Noggrant utvalda råvaror, lagade med kärlek och omsorg. Beställ för avhämtning eller leverans.")}
            </p>
          </div>
          {/* Navigation */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4B483] mb-5">{t("Navigation")}</p>
            <ul className="space-y-3">
              {links.map(({ id, to, label }) => (
                <li key={id}>
                  <Link to={to} className="text-sm text-[#D4D4D8] hover:text-[#EDEDED] transition-colors">{label}</Link>
                </li>
              ))}
              <li>
                <Link to={user ? "/profile" : "/auth"} className="text-sm text-[#D4D4D8] hover:text-[#EDEDED] transition-colors">
                  {user ? t("Profil") : t("Logga in")}
                </Link>
              </li>
            </ul>
          </div>
          {/* Information */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4B483] mb-5">{t("Information")}</p>
            <ul className="space-y-3">
              <li><Link to="/privacy-policy" className="text-sm text-[#D4D4D8] hover:text-[#EDEDED] transition-colors">{t("Integritetspolicy")}</Link></li>
              <li><Link to="/terms-of-service" className="text-sm text-[#D4D4D8] hover:text-[#EDEDED] transition-colors">{t("Användarvillkor")}</Link></li>
              <li><Link to="/about-us" className="text-sm text-[#D4D4D8] hover:text-[#EDEDED] transition-colors">{t("Om oss")}</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-[#1A1A1A] flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#3F3F46]">
            © {new Date().getFullYear()} Bästa Biten
          </p>
          <div className="h-px w-24 bg-gradient-to-r from-transparent via-[#D4B483]/30 to-transparent hidden sm:block" />
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#3F3F46]">Olofström · Karlshamn</p>
        </div>
      </div>
    </footer>
  );
}

function CustomerLayout() {
  const { t } = useApp();
  const [restaurantStatus, setRestaurantStatus] = useState(null);

  useEffect(() => {
    let mounted = true;
    let timerId;

    const scheduleNextCheck = (isOpen) => {
      clearTimeout(timerId);
      const nextDelay = isOpen ? 12000 : 4000;
      timerId = setTimeout(loadStatus, nextDelay);
    };

    const loadStatus = async () => {
      try {
        const res = await api.get("/menu/restaurant-info");
        if (!mounted) return;
        setRestaurantStatus(res.data);
        scheduleNextCheck(Boolean(res.data?.is_open));
      } catch {
        if (!mounted) return;
        setRestaurantStatus({
          is_open: true,
          closed_message: "",
        });
        scheduleNextCheck(true);
      }
    };

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        loadStatus();
      }
    };

    const refreshOnFocus = () => {
      loadStatus();
    };

    loadStatus();
    window.addEventListener("focus", refreshOnFocus);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      mounted = false;
      clearTimeout(timerId);
      window.removeEventListener("focus", refreshOnFocus);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  const isClosed = restaurantStatus && !restaurantStatus.is_open;

  return (
    <div className="app-shell bg-[#0A0A0A] site-ambient">
      <Header />
      {isClosed && (
        <div className="fixed top-16 md:top-20 left-0 right-0 z-40 px-4 py-2 bg-[#FB923C]/15 border-b border-[#FB923C]/40 text-[#FDBA74] text-xs text-center font-semibold backdrop-blur-sm">
          {restaurantStatus.closed_message || t("We're closed right now and not accepting orders.")}
        </div>
      )}
      <CartSheet />
      <CookieBanner />
      <main className={`app-main ${isClosed ? "app-main-closed" : "app-main-open"} relative z-[1]`}>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

// Reset scroll to the top on every navigation (SPA route change keeps the old
// scroll position by default, which feels broken when opening a product).
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function App() {
  const { theme } = useTheme();
  return (
    <AppProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster position="top-center" richColors theme={theme} duration={5000} closeButton />
        <Routes>
          <Route element={<CustomerLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/order/:id" element={<OrderTrackingPage />} />
            <Route path="/orders" element={<OrderHistoryPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/about-us" element={<AboutUsPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          </Route>
          <Route path="/manage/login" element={<AdminLoginPage />} />
          <Route path="/manage" element={<AdminDashboard />} />
          <Route path="/admin/*" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
